import { Directive, ElementRef, EventEmitter, inject, Output } from '@angular/core';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { POSITION_MAP, POSITION_TYPE } from '@shared/utils/common/cdk-position-map';
import { KeyboardShortcutsListenerService } from '@dta/ui/services/keyboard-shortcuts-listener/keyboard-shortcuts-listener.service';
import { KeyboardActionModeType } from '@dta/shared/models/keyboard-shortcut.model';

@Directive({
  selector: '[loopEmoji]',
  host: {
    '(click)': 'toggleEmojiPicker()'
  }
})
export class LoopEmojiDirective {
  private readonly overlay: Overlay = inject(Overlay);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly keyboardShortcutsListenerService: KeyboardShortcutsListenerService = inject(
    KeyboardShortcutsListenerService
  );

  overlayRef?: OverlayRef;
  emojiClickSubscription?: Subscription;

  @Output() readonly emojiClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() readonly emojiClick: EventEmitter<string> = new EventEmitter<string>();

  protected toggleEmojiPicker(): void {
    if (this.overlayRef) {
      this.close();
      return;
    }

    const positionStrategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions(['topLeft', 'topRight', 'bottomLeft', 'bottomRight'].map(e => POSITION_MAP[e as POSITION_TYPE]));

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });

    const portal = new ComponentPortal(PickerComponent);
    const componentRef = this.overlayRef.attach(portal);
    Object.entries({
      perLine: 8,
      totalFrequentLines: 1,
      showPreview: false,
      darkMode: false
    }).map(([key, value]) => {
      componentRef.instance[key] = value;
    });

    // Let Picker open, then update position
    setTimeout(() => {
      this.overlayRef.updatePosition();
    });
    this.keyboardShortcutsListenerService.setMode(KeyboardActionModeType.SEARCH);

    this.emojiClickSubscription = componentRef.instance.emojiClick.pipe(take(1)).subscribe(emoji => {
      this.emojiClick.emit(emoji.emoji.native);
      this.close();
    });

    this.overlayRef.backdropClick().subscribe(() => this.close());
  }

  private close(): void {
    /**
     * Revert only in relation to SEARCH — if the target input was refocused before
     * close (emoji click refocuses the editor), TEXT has already replaced SEARCH on
     * the stack and a blind goToPreviousMode() would drop the focused input back
     * into NAVIGATION mode, swallowing shortcut keys (e.g. R) while typing.
     */
    this.keyboardShortcutsListenerService.goToPreviousMode(KeyboardActionModeType.SEARCH);
    this.emojiClickSubscription?.unsubscribe();
    this.overlayRef.dispose();
    this.overlayRef = undefined;
    this.emojiClose.emit();
  }
}
